/**
 * Serper sweep query catalog — must stay aligned with supabase/functions/run-sweep/index.ts
 * (SERPER_FIELD_ORDER + segmentForField).
 */
import { format, parseISO, isValid } from "date-fns";

export const SERPER_Q_MAX = 800;
export const SERPER_DEFAULT_GL = "us";
export const SERPER_DEFAULT_HL = "en";

export const SERPER_MODES = ["news", "search"];

/** Default matches DB migration and run-sweep. */
export const DEFAULT_SERPER_MODE = "news";

export function normalizeSerperMode(raw) {
  const m = raw == null ? "" : String(raw).trim().toLowerCase();
  if (m === "search") return "search";
  return "news";
}

/** Fixed segment order for composed q (must match run-sweep). */
export const SERPER_FIELD_ORDER = [
  "state",
  "chamber",
  "district",
  "team",
  "markets",
  "election_type",
  "election_date",
  "runoff_date",
  "status",
  "ge_rating",
  "race_importance",
  "reference_link",
  "advertiser_name",
  "notes",
];

export const SERPER_FIELD_LABELS = {
  state: "State",
  chamber: "Level of office",
  district: "District / race",
  team: "Team",
  markets: "Markets",
  election_type: "Election type",
  election_date: "Primary date",
  runoff_date: "Runoff date",
  status: "Status",
  ge_rating: "GE rating",
  race_importance: "Race importance (1–5)",
  reference_link: "Reference link",
  advertiser_name: "Advertiser name",
  notes: "Notes (truncated in query)",
};

/** Max number of columns that can be selected at once. */
export const SERPER_MAX_SELECTED_FIELDS = SERPER_FIELD_ORDER.length;

export function normalizeSerperQueryFields(raw) {
  const allowed = new Set(SERPER_FIELD_ORDER);
  const picked = new Set();
  if (Array.isArray(raw)) {
    for (const x of raw) {
      const k = String(x).trim();
      if (allowed.has(k)) picked.add(k);
    }
  }
  if (picked.size === 0) picked.add("district");
  return SERPER_FIELD_ORDER.filter((f) => picked.has(f));
}

function t(v) {
  const s = v == null ? "" : String(v).trim();
  return s || null;
}

function formatRaceDate(val) {
  if (val == null || val === "") return null;
  try {
    const d = typeof val === "string" ? parseISO(String(val).slice(0, 10)) : new Date(val);
    if (!isValid(d)) return t(val);
    return format(d, "MMM d, yyyy");
  } catch {
    return t(val);
  }
}

/**
 * @param {Record<string, unknown>} form — race-shaped row
 * @param {string} advertiserNameForQuery
 */
export function buildSerperQueryPreview(form, advertiserNameForQuery) {
  const fields = normalizeSerperQueryFields(form.serper_query_fields);
  const parts = [];
  for (const f of fields) {
    let seg = null;
    switch (f) {
      case "state":
        seg = t(form.state);
        break;
      case "chamber":
        seg = t(form.chamber);
        break;
      case "district":
        seg = t(form.district);
        break;
      case "team":
        seg = t(form.team);
        break;
      case "markets":
        seg = t(form.markets);
        break;
      case "election_type":
        seg = t(form.election_type);
        break;
      case "election_date":
        seg = formatRaceDate(form.election_date);
        break;
      case "runoff_date":
        seg = formatRaceDate(form.runoff_date);
        break;
      case "status":
        seg = t(form.status);
        break;
      case "ge_rating":
        seg = t(form.ge_rating);
        break;
      case "race_importance":
        if (form.race_importance != null && form.race_importance !== "") {
          seg = String(form.race_importance).trim();
        }
        break;
      case "reference_link": {
        const ref = t(form.reference_link);
        if (!ref) break;
        seg = ref.length > 120 ? `${ref.slice(0, 117)}…` : ref;
        break;
      }
      case "advertiser_name":
        seg = t(advertiserNameForQuery);
        break;
      case "notes": {
        const n = t(form.notes);
        if (!n) break;
        seg = n.length > 160 ? `${n.slice(0, 157)}…` : n;
        break;
      }
      default:
        break;
    }
    if (seg) parts.push(seg);
  }
  const q = parts.join(" ").replace(/\s+/g, " ").trim();
  if (q.length <= SERPER_Q_MAX) return q;
  return `${q.slice(0, SERPER_Q_MAX)}…`;
}

export function effectiveSerperGlHl(savedGl, savedHl) {
  const gl = (savedGl != null && String(savedGl).trim()) || SERPER_DEFAULT_GL;
  const hl = (savedHl != null && String(savedHl).trim()) || SERPER_DEFAULT_HL;
  return { gl, hl };
}

/**
 * @param {Record<string, unknown>} item — Serper organic entry
 * @param {number} index
 */
export function normalizeSerperOrganicItem(item, index) {
  const link = String(item?.link ?? "");
  let displayLink = "";
  try {
    if (link) displayLink = new URL(link).hostname.replace(/^www\./i, "");
  } catch {
    displayLink = "";
  }
  return {
    title: item?.title != null ? String(item.title) : "",
    link,
    snippet: item?.snippet != null ? String(item.snippet) : "",
    displayLink,
    position: typeof item?.position === "number" ? item.position : index + 1,
    source: "serper",
    serper_endpoint: "search",
  };
}

/**
 * @param {Record<string, unknown>} item — Serper news API row
 * @param {number} index
 */
export function normalizeSerperNewsItem(item, index) {
  const link = String(item?.link ?? "");
  let displayLink = "";
  try {
    if (link) displayLink = new URL(link).hostname.replace(/^www\./i, "");
  } catch {
    displayLink = "";
  }
  const publisher = item?.source != null ? String(item.source) : "";
  const newsDate = item?.date != null ? String(item.date) : "";
  return {
    title: item?.title != null ? String(item.title) : "",
    link,
    snippet: item?.snippet != null ? String(item.snippet) : "",
    displayLink: displayLink || publisher,
    position: typeof item?.position === "number" ? item.position : index + 1,
    source: "serper",
    serper_endpoint: "news",
    publisher,
    news_date: newsDate,
  };
}
